import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { captureLead } from "@/lib/api";
import { Loader2, Gift } from "lucide-react";
import { toast } from "sonner";

export const EmailPopup = ({ onCode }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [code, setCode] = useState("");

  useEffect(() => {
    if (localStorage.getItem("um_popup_seen")) return;
    const t = setTimeout(() => setOpen(true), 6000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    localStorage.setItem("um_popup_seen", "1");
    setOpen(false);
  };

  const submit = async () => {
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setLoading(true);
    try {
      const res = await captureLead(email);
      setCode(res.code);
      setDone(true);
      onCode && onCode(res.code);
      localStorage.setItem("um_popup_seen", "1");
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && dismiss()}>
      <DialogContent className="sm:max-w-lg rounded-none border-black/10 p-0 overflow-hidden" data-testid="email-popup">
        <DialogTitle className="sr-only">Get 10% off your first guide</DialogTitle>
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="hidden md:block bg-[#0a0a0a] p-8 text-white">
            <Gift className="h-8 w-8" />
            <p className="mt-8 font-display text-4xl leading-none tracking-tight">10% off<br />your first<br />guide.</p>
            <p className="mt-6 text-sm text-white/60">Join thousands upgrading their life with UpgradeMate.</p>
          </div>
          <div className="p-8">
            {!done ? (
              <>
                <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Exclusive offer</p>
                <h3 className="mt-2 font-display text-2xl tracking-tight">Get 10% off</h3>
                <p className="mt-2 text-sm text-muted-foreground">Enter your email and we'll send your discount code instantly.</p>
                <Input data-testid="popup-email-input" type="email" placeholder="you@email.com" value={email}
                  onChange={(e) => setEmail(e.target.value)} className="mt-5 rounded-none h-11 border-black/20 focus-visible:ring-black" />
                <Button data-testid="popup-submit" onClick={submit} disabled={loading}
                  className="mt-3 w-full rounded-none h-11 bg-primary text-primary-foreground hover:bg-primary/90">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send my code"}
                </Button>
                <button onClick={dismiss} className="mt-3 w-full text-center text-xs text-muted-foreground hover:text-foreground" data-testid="popup-dismiss">No thanks</button>
              </>
            ) : (
              <div className="flex h-full flex-col justify-center">
                <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Your code</p>
                <p className="mt-2 font-display text-3xl tracking-tight" data-testid="popup-code">{code}</p>
                <p className="mt-2 text-sm text-muted-foreground">Use it at checkout for 10% off. Also sent to your inbox.</p>
                <Button onClick={dismiss} className="mt-5 rounded-none h-11 bg-primary text-primary-foreground" data-testid="popup-close">Start shopping</Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
